import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Column,
  FormController,
  MuiBox,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../../../../../components";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../../../helpers/constants";
import {
  getFormattedAmount,
  isEmpty,
  isValueIncludedIgnoreCase,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  ProjectFeesAndExpenseRow,
  ProjectInvoice,
  ProjectRaiseInvoiceFormKey,
  ProjectRaiseInvoiceFormType,
} from "../../../../../../modelsAndSchemas/Models";
import { projectRaiseInvoiceSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.RAISE_INVOICE}`;

type ProjectRaiseInvoiceListProps = {
  projectInvoiceFeesAndExpense: ProjectFeesAndExpenseRow[];
  currencyName: string;
};

export const ProjectRaiseInvoiceList: FC<ProjectRaiseInvoiceListProps> = ({
  projectInvoiceFeesAndExpense,
  currencyName,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const invoiceTableRef = useRef<{ resetPagination: () => void }>(null);

  const getProjectLabel = (key: keyof typeof projectRaiseInvoiceSiteCoreKeys) => {
    return fetchLabel(
      projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
      projectRaiseInvoiceSiteCoreKeys[key],
    );
  };
  const { control } = useForm<Partial<ProjectInvoice>>();

  const [state, setState] = useState<{
    filteredListInvoice: ProjectRaiseInvoiceFormType[];
    originalListInvoice: ProjectRaiseInvoiceFormType[];
  }>({
    filteredListInvoice: [],
    originalListInvoice: [],
  });

  const uniqueTypesInvoices = Array.from(
    new Set(state.originalListInvoice.map((item) => item.type)),
  );

  useEffect(() => {
    let transformedData: ProjectRaiseInvoiceFormType[] = [];

    projectInvoiceFeesAndExpense.forEach((elementObj) => {
      const {
        name: feeOrExpenseName,
        typeName: type,
        expenseCategoryName: category,
        billingAmount,
        tax: taxAmount,
        feeOrExpenseType,
      } = elementObj;

      transformedData.push({
        feeOrExpenseName: feeOrExpenseName,
        type: type,
        category: category,
        billingAmount: String(billingAmount || 0),
        taxAmount: String(taxAmount || 0),
        feeOrExpenseType: feeOrExpenseType,
        totalInvoiceAmount: String(
          Number(billingAmount || 0) + Number(taxAmount || 0),
        ),
        lineNumber: (transformedData.length + 1).toString(),
      });
    });

    setState((prevState) => ({
      ...prevState,
      originalListInvoice: transformedData,
      filteredListInvoice: transformedData,
    }));
  }, [projectInvoiceFeesAndExpense]);

  const filters = useWatch({ control });

  useEffect(() => {
    appliedFilters(state.originalListInvoice);
  }, [filters]);

  const appliedFilters = (projectInvoiceData: ProjectRaiseInvoiceFormType[] = []) => {
    if (isEmpty(projectInvoiceData)) return;
    let filteredListInvoice = [...projectInvoiceData];

    const {
      billingAmount,
      category,
      feeOrExpenseName,
      feeOrExpenseType,
      taxAmount,
      totalInvoiceAmount,
      type,
      lineNumber,
    } = filters || {};

    filteredListInvoice = filteredListInvoice.filter((item) => {
      return (
        isValueIncludedIgnoreCase(item.billingAmount, billingAmount) &&
        isValueIncludedIgnoreCase(item.category, category) &&
        isValueIncludedIgnoreCase(item.feeOrExpenseName, feeOrExpenseName) &&
        isValueIncludedIgnoreCase(item.feeOrExpenseType, feeOrExpenseType) &&
        isValueIncludedIgnoreCase(item.taxAmount, taxAmount) &&
        isValueIncludedIgnoreCase(item.type, type) &&
        isValueIncludedIgnoreCase(
          item.totalInvoiceAmount,
          totalInvoiceAmount,
        ) &&
        isValueIncludedIgnoreCase(item.lineNumber, lineNumber)
      );
    });

    setState((prevState: any) => ({
      ...prevState,
      filteredListInvoice: filteredListInvoice,
    }));
    invoiceTableRef.current?.resetPagination();
  };

  const getAmountWithCurrency = (
    amount: string | number,
    currencyName: string,
  ) => {
    if (!amount || isNaN(Number(amount))) return EMPTY_VALUE_DASH;
    return `${currencyName} ${getFormattedAmount(Number(amount))}`;
  };

  const columns: Column[] = [
    {
      id: ProjectRaiseInvoiceFormKey.projectLineNumber,
      label: getProjectLabel("invoiceLineNumberTableColumn"),
      isSortVisible: true,
      render: (row: any, index?: number) => {
        return <MuiTypography>{row.lineNumber}</MuiTypography>;
      },
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectFeeOrExpenseName,
      label: getProjectLabel("feeOrExpenseNameTableColumn"),
      isSortVisible: true,
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectType,
      label: getProjectLabel("typeTableColumn"),
      isSortVisible: true,
      minWidth: 200,
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectCategory,
      label: getProjectLabel("categoryTableColumn"),
      isSortVisible: true,
      minWidth: 200,
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectFeeOrExpenseType,
      label: getProjectLabel("feeOrExpenseTypeTableColumn"),
      isSortVisible: true,
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectBillingAmount,
      label: getProjectLabel("billingAmountTableColumn"),
      isSortVisible: true,
      render: (row: any) =>
        getAmountWithCurrency(row.billingAmount, currencyName),
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectTaxAmount,
      label: getProjectLabel("taxAmountTableColumn"),
      isSortVisible: true,
      render: (row: any) => getAmountWithCurrency(row.taxAmount, currencyName),
    },
    {
      id: ProjectRaiseInvoiceFormKey.projectTotalInvoiceAmount,
      label: getProjectLabel("totalInvoiceAmountTableColumn"),
      isSortVisible: true,
      render: (row: any) =>
        getAmountWithCurrency(row.totalInvoiceAmount, currencyName),
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectLineNumber}
      key={ProjectRaiseInvoiceFormKey.projectLineNumber}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectLineNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.lineNumber}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectFeeOrExpenseName}
      key={ProjectRaiseInvoiceFormKey.projectFeeOrExpenseName}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectFeeOrExpenseName}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.feeOrExpenseName}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectType}
      key={ProjectRaiseInvoiceFormKey.projectType}
    >
      <MuiSelect
        label=""
        options={uniqueTypesInvoices.map((type) => ({ label: type, value: type }))}
        visibleDefaultSelectOption={true}
        value={filters.type}
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectType}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectCategory}
      key={ProjectRaiseInvoiceFormKey.projectCategory}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectCategory}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.category}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectFeeOrExpenseType}
      key={ProjectRaiseInvoiceFormKey.projectFeeOrExpenseType}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectFeeOrExpenseType}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.feeOrExpenseType}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectBillingAmount}
      key={ProjectRaiseInvoiceFormKey.projectBillingAmount}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectBillingAmount}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.billingAmount}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectTaxAmount}
      key={ProjectRaiseInvoiceFormKey.projectTaxAmount}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectTaxAmount}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.taxAmount}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectRaiseInvoiceFormKey.projectTotalInvoiceAmount}
      key={ProjectRaiseInvoiceFormKey.projectTotalInvoiceAmount}
    >
      <MuiTextField
        id={`${pageId}-${ProjectRaiseInvoiceFormKey.projectTotalInvoiceAmount}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        value={filters.totalInvoiceAmount}
        showCloseIcon
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  return (
    <MuiBox data-testid="project-raise-invoice-list">
      <MuiTable
        ref={invoiceTableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={
          state.filteredListInvoice as unknown as Record<string, ReactNode>[]
        }
        noDataInApi={state.originalListInvoice.length === 0}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
